import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { ArrowBack, Close, SkipNextRounded } from '@mui/icons-material';
import { MenuIcon } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { MainAppReducer } from 'Types';
// import { Levels } from '../../Constants/LevelData'
import LeftArrowIcon from '../../assets/images/LeftArrowIcon.png';
import Chevron1 from '../../assets/images/Chevron1.png';
import ChevronSingle from '../../assets/images/ChevronSingle.png';
import { useState, useEffect, useCallback } from 'react';
import { useWakeLock } from 'Hooks/useWakeLock';
import * as appActions from 'Actions/app';
import * as eventActions from 'Actions/events';
import * as lessonActions from 'Actions/lesson';
import './Tutorial.css';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { v4 as uuid } from 'uuid';
import Fullscreen from 'Components/Fullscreen';
import { TutorialData2 } from 'Types';
import InteractiveTemplate1 from './Templates/InteractiveTemplate1';
import { MIN_NUM_PHRASES } from 'Utils/Constants';
import TutorialData from 'Constants/Tutorial';
import { isCapacitorNative } from 'Utils/Constants';
import axios from 'axios';
import { AppDispatch, type RootState } from 'Reducers';
import * as Immutable from 'immutable';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { NotifyPhaser, NotifyPhaserEvents } from 'Phaser/EventBus2';
import LessonDidNotLoad from 'Components/LessonDidNotLoad';
import { useStalledFor } from 'Hooks/useStalledFor';
import { useDidNotLoadMessageAllowed } from 'Hooks/useDidNotLoadMessageAllowed';
import { reportError } from 'Utils/reportError';
import { usePressedState } from 'Utils/usePressedState';
import { SKIP_TUTORIAL_LINE_PX, skipTutorialTouchSx } from './skipTutorialTouch';
import { useTutorialBack } from './useTutorialBack';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';

// MUS-651: a tutorial whose data has not arrived in this long (visible time
// only) shows a message with Retry and the way back instead of an empty screen.
export const TUTORIAL_LOAD_TIMEOUT_MS = 20000;
export const TUTORIAL_REQUEST_TIMEOUT_MS = 15000;

// Keep in sync with the breakpoint used in InteractiveTemplate1.tsx / Chapters.tsx
const mobileQuery = '(max-width: 768px), (max-height: 500px)';

const Tutorial = () => {
  const _location = useLocation();
  const auth = useAuthWrapperContext();
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const levelSelect = data.levelSelect;
  const [currentCarousel, setCurrentCarousel] = useState(1);
  const curCarousel = data.tutorialData[currentCarousel - 1];
  const totalCarousels = data.tutorialData.length;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  useWakeLock();

  useEffect(() => {
    // init
    dispatch(lessonActions.updatePhrasesTilNextTier(MIN_NUM_PHRASES));
    dispatch(lessonActions.setTierSummary(null));
    dispatch(lessonActions.setLessonProcessPending(false));
  }, [dispatch]);

  // A tutorial starts on entering /tutorial and again on each level changed
  // inside it (finishing level 0 moves straight on to level 1's tutorial).
  const startedLevelNumber = data.levelData[levelSelect].level_number;
  useEffect(() => {
    dispatch(eventActions.tutorialStartedEventAction(startedLevelNumber));
  }, [dispatch, startedLevelNumber]);

  // comment in the lines with setTutorialOpacity1 & 2 ( and set defaults below to 0 to show the transition effect)
  // Note that I will need to use ref and clear the animation
  // so that a user can go through different pages and it will resstart
  // these timeouts for transition
  const [tutorialData, setTutorialData] = useState<TutorialData2 | null>(null);
  const [tutorialOpacity1, setTutorialOpacity1] = useState(1);
  const [tutorialOpacity2, setTutorialOpacity2] = useState(1);
  useEffect(() => {
    if (tutorialOpacity1 == 0 && tutorialOpacity2 == 0) {
      setTimeout(() => {
        setTutorialOpacity1(1);
      }, 500);
      setTimeout(() => {
        setTutorialOpacity2(1);
      }, 3000);
    }
  }, [currentCarousel]);

  const decTutorial = useCallback(() => {
    if (currentCarousel > 1) {
      setCurrentCarousel(currentCarousel - 1);
    }
  }, [currentCarousel]);

  const incTutorial = useCallback(() => {
    if (currentCarousel < totalCarousels) {
      setCurrentCarousel(currentCarousel + 1);
    } else {
      console.log('[Tutorial] incTutorial navigating to lesson - cleaning up');
      NotifyPhaserEvents.UnloadExercise();
      setTimeout(() => {
        navigate(`/lesson`);
      }, 100);
    }
  }, [currentCarousel, totalCarousels]);

  useEffect(() => {
    localStorage.setItem('hasReloaded', 'false');
  }, []);

  useEffect(() => {
    dispatch(appActions.fetchTutorialData());
  }, [data.levelSelect]);
  const level = data.levelData[levelSelect].level_number;
  // Levels with a video tutorial use it; the older page-by-page tutorial is for the rest.
  const showInteractiveTutorial = level < TutorialData.length;

  // Use arrow keys to navigate the tutoiral
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') decTutorial();
      if (e.key === 'ArrowRight') incTutorial();
    },
    [decTutorial, incTutorial],
  );

  // Only the page-by-page tutorial has pages. Under a video tutorial the arrows used to move
  // through hidden pages, and past the last one (or with none) leave for the lesson.
  useEffect(() => {
    if (showInteractiveTutorial) return undefined;
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, showInteractiveTutorial]);

  // MUS-651: a failed or stalled tutorial-data request used to leave the area
  // under the header empty, with nothing to retry it.
  const [tutorialDataFailed, setTutorialDataFailed] = useState(false);
  const [tutorialLoadAttempt, setTutorialLoadAttempt] = useState(0);
  // Not over the offline screen or the paywall, which each give the way on.
  const { allowed: didNotLoadMessageAllowed, noteFailure } = useDidNotLoadMessageAllowed(
    data.userData?.subscription_status,
  );
  const requestTurorialData = useCallback(async () => {
    setTutorialDataFailed(false);
    try {
      const levelNumber = data.levelData[levelSelect].level_number;
      const token = await auth.currentAuthToken();
      const options = {
        url: process.env.REACT_APP_BACKEND_URL + `/api/v1/tutorial-data?levelNumber=${levelNumber}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: TUTORIAL_REQUEST_TIMEOUT_MS,
      };
      const res = await axios.request(options);
      setTutorialData({
        level_number: levelNumber,
        objective_data: res.data.data,
        tutorial_config: TutorialData[levelNumber],
      });
    } catch (error) {
      reportError(error, 'tutorial.data');
      noteFailure(error);
      setTutorialDataFailed(true);
    }
  }, [data.levelData, levelSelect, auth, noteFailure]);

  useEffect(() => {
    requestTurorialData();
  }, [data.levelSelect, requestTurorialData]);

  const _currEnv = process.env.REACT_APP_NODE_ENV;
  const [chaptersVisible, setChaptersVisible] = useState(false);
  // On a phone the chapters slide in over the tutorial; Android's Back closes them as the header's
  // X does. On a larger screen they are a fixed sidebar, not something to close.
  const chaptersSlideIn = useMediaQuery(mobileQuery);
  // They are drawn in the page, under the header, not over it.
  useHardwareBackClosesLayer(
    chaptersSlideIn && chaptersVisible,
    () => setChaptersVisible(false),
    0,
  );

  const backOnePage = useCallback(() => {
    console.log('[Tutorial Footer] Back button clicked - cleaning up');
    // Send unload event to clean up any active Phaser scenes
    NotifyPhaserEvents.UnloadExercise();
    decTutorial();
  }, [decTutorial]);
  // The footer's Back is only on the older page-by-page tutorial, past its first page.
  const leaveTutorial = useTutorialBack({
    pageBackShown: !showInteractiveTutorial && currentCarousel > 1,
    backOnePage,
  });

  const tutorialProcessingStalled =
    useStalledFor(
      !!data.tutorialDataProcessing,
      TUTORIAL_LOAD_TIMEOUT_MS,
      String(tutorialLoadAttempt),
    ) && didNotLoadMessageAllowed;
  const tutorialDataStalled = useStalledFor(
    showInteractiveTutorial && tutorialData === null,
    TUTORIAL_LOAD_TIMEOUT_MS,
    String(tutorialLoadAttempt),
  );
  const showTutorialDidNotLoad =
    didNotLoadMessageAllowed &&
    (tutorialProcessingStalled ||
      (showInteractiveTutorial &&
        tutorialData === null &&
        (tutorialDataFailed || tutorialDataStalled)));
  const retryTutorialLoad = useCallback(() => {
    setTutorialLoadAttempt((attempt) => attempt + 1);
    if (data.tutorialDataProcessing) dispatch(appActions.fetchTutorialData());
    requestTurorialData();
  }, [data.tutorialDataProcessing, dispatch, requestTurorialData]);
  const leaveTutorialForRoadmap = useCallback(() => {
    NotifyPhaserEvents.UnloadExercise();
    navigate('/roadmap');
  }, [navigate]);
  const tutorialDidNotLoad = (
    <LessonDidNotLoad onRetry={retryTutorialLoad} onBackToRoadmap={leaveTutorialForRoadmap} />
  );

  return data.tutorialDataProcessing ? (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {tutorialProcessingStalled ? tutorialDidNotLoad : <CustomCircularProgress />}
    </Box>
  ) : (
    <>
      {showInteractiveTutorial && (
        <Box
          id="tutorial-container"
          sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
        >
          <Header
            chaptersVisible={chaptersVisible}
            setChaptersVisible={setChaptersVisible}
            onBack={leaveTutorial}
          />
          <Box sx={{ flex: 1, minHeight: 0, height: 0, position: 'relative' }}>
            {showTutorialDidNotLoad && tutorialDidNotLoad}
            {tutorialData && (
              <InteractiveTemplate1
                key={tutorialData.level_number}
                tutorialData={tutorialData}
                chaptersVisible={chaptersVisible}
              />
            )}
          </Box>
        </Box>
      )}
      {/* The page-by-page tutorial, only for a level with no video tutorial. It used to be drawn
          under every video tutorial too, out of sight, its footer still taking the arrow keys. */}
      {!showInteractiveTutorial && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header
            chaptersVisible={chaptersVisible}
            setChaptersVisible={setChaptersVisible}
            onBack={leaveTutorial}
          />
          <Box sx={{ minHeight: '400px', height: '100%' }}>
            {curCarousel?.template_number == 1 && (
              <Template1
                title={curCarousel.title}
                points={curCarousel.info_text.split('|')}
                srcs={curCarousel.srcs.split('|')}
              />
            )}
            {curCarousel?.template_number == 2 && (
              <Template2
                title={curCarousel.title}
                points={curCarousel.info_text.split('|')}
                srcs={curCarousel.srcs.split('|')}
              />
            )}
            {curCarousel?.template_number == 3 && (
              <Template3
                title={curCarousel.title}
                points={curCarousel.info_text.split('|')}
                srcs={curCarousel.srcs.split('|')}
              />
            )}
            {curCarousel?.template_number == 4 && (
              <Template4
                title={curCarousel.title}
                points={curCarousel.info_text.split('|')}
                srcs={curCarousel.srcs.split('|')}
              />
            )}
            {curCarousel?.template_number == 5 && (
              <Template5
                title={curCarousel.title}
                points={curCarousel.info_text.split('|')}
                srcs={curCarousel.srcs.split('|')}
              />
            )}
            {curCarousel?.template_number == 6 && (
              <Template6
                title={curCarousel.title}
                points={curCarousel.info_text.split('|')}
                srcs={curCarousel.srcs.split('|')}
                tutorialOpacity1={tutorialOpacity1}
                tutorialOpacity2={tutorialOpacity2}
              />
            )}
          </Box>
          <Footer
            currentCarousel={currentCarousel}
            setCurrentCarousel={setCurrentCarousel}
            totalCarousels={totalCarousels}
            setTutorialOpacity1={setTutorialOpacity1}
            setTutorialOpacity2={setTutorialOpacity2}
            onBackOnePage={backOnePage}
            incTutorial={incTutorial}
          />
        </Box>
      )}
    </>
  );
};

type TemplateProps = { title?: string; points?: string[]; srcs?: string[] };

// These are the layouts that will be rendered!!!
const Template1 = (props: TemplateProps) => {
  const { title, points, srcs } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
      }}
    >
      <Box sx={{ width: '50%', margin: '20px' }}>
        <img
          style={{
            width: '100%',
            backgroundColor: '#E2E2E2',
            borderRadius: '10px',
          }}
          src={`https://${srcs?.[0]}`}
        />
      </Box>
      <Box sx={{ width: '50%', margin: '20px' }}>
        {/* Template 1 */}
        <Box sx={{ marginLeft: '30px', marginRight: '30px', textAlign: 'left' }}>
          <Box
            sx={{
              marginBottom: '10px',
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '34px',
              lineHeight: '41px',
              letterSpacing: '0.01em',
              color: '#CED0D4',
            }}
          >
            {title}
          </Box>
          {TextBox(points ?? [])}
        </Box>
      </Box>
    </Box>
  );
};

const Template2 = (props: TemplateProps) => {
  const { title, points, srcs } = props;
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '0px 20px 0px 20px',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
      }}
    >
      <Box
        sx={{
          maxHeight: '40%',
          backgroundColor: '#E2E2E2',
          borderRadius: '5px',
          margin: '0px auto 10px auto',
          padding: '10px 6px 10px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img style={{ maxHeight: '100%', maxWidth: '100%' }} src={`https://${srcs[0]}`} />
      </Box>
      <Box
        sx={{
          margin: '0 auto auto 0',
          minHeight: `${50 * points.length}px`,
          height: `${50 * points.length}px`,
        }}
      >
        <Box sx={{ margin: '20px', textAlign: 'left' }}>
          <Box
            sx={{
              marginBottom: '10px',
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '34px',
              lineHeight: '41px',
              letterSpacing: '0.01em',
              color: '#CED0D4',
            }}
          >
            {title}
          </Box>
          {TextBox(points)}
        </Box>
      </Box>
    </Box>
  );
};

const Template3 = (props: TemplateProps) => {
  const { title, points, srcs } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
      }}
    >
      <Box
        sx={{
          maxHeight: '280px',
          margin: '0px 20px 10px 20px',
          overflow: 'hidden',
        }}
      >
        <img style={{ maxHeight: '100%', maxWidth: '100%' }} src={`https://${srcs[0]}`} />
      </Box>
      <Box
        sx={{
          minHeight: `${50 * points.length}px`,
          height: `${50 * points.length}px`,
        }}
      >
        <Box sx={{ margin: '20px 40px 40px 40px', textAlign: 'left' }}>
          <Box
            sx={{
              marginBottom: '10px',
              fontStyle: 'normal',
              fontWeight: '400',
              fontSize: '34px',
              lineHeight: '41px',
              letterSpacing: '0.01em',
              color: '#CED0D4',
            }}
          >
            {title}
          </Box>
          {TextBox(points)}
        </Box>
      </Box>
    </Box>
  );
};

const Template4 = (props: TemplateProps) => {
  const { title, points, srcs } = props;
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
      }}
    >
      <Box
        sx={{
          height: '50%',
          maxHeight: '280px',
          margin: '0px 20px 10px 20px',
          overflow: 'hidden',
        }}
      >
        <img
          style={{
            maxHeight: '100%',
            maxWidth: '100%',
            backgroundColor: '#E2E2E2',
            borderRadius: '10px',
          }}
          src={`https://${srcs[0]}`}
        />
      </Box>
      <Box sx={{ margin: '0 auto auto 0', minHeight: '250px', height: '50%' }}>
        <Box sx={{ textAlign: 'left', display: 'flex', height: '100%' }}>
          <Box sx={{ margin: '30px 0px 30px 60px' }}>
            <img
              style={{
                maxHeight: '100%',
                borderRadius: '10px',
                backgroundColor: '#E2E2E2',
              }}
              src={`https://${srcs[1]}`}
            />
          </Box>
          <Box sx={{ margin: '30px' }}>
            <Box
              sx={{
                fontStyle: 'normal',
                marginBottom: '10px',
                fontWeight: '400',
                fontSize: '34px',
                lineHeight: '41px',
                letterSpacing: '0.01em',
                color: '#CED0D4',
              }}
            >
              {title}
            </Box>
            {TextBox(points)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Template5 = (props: TemplateProps) => {
  const { title, points, srcs } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
      }}
    >
      <Box
        sx={{
          margin: '10px 30px 9px 30px',
          maxHeight: '65%',
          maxWidth: '100%',
          backgroundColor: '#E2E2E2',
          borderRadius: '5px',
          padding: '10px 6px 20px 0px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            margin: '5px 10px 0px 10px',
            maxHeight: '50%',
            maxWidth: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            style={{ maxHeight: '100%', maxWidth: '50%', marginRight: '4px' }}
            src={`https://${srcs[0]}`}
          />
          <img
            style={{ maxHeight: '100%', maxWidth: '50%', marginLeft: '4px' }}
            src={`https://${srcs[1]}`}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            maxWidth: '50%',
            maxHeight: '50%',
            justifyContent: 'center',
          }}
        >
          <img style={{ maxWidth: '100%' }} src={`https://${srcs[2]}`} />
        </Box>
      </Box>
      {/* Template 5 */}
      <Box sx={{ height: '50%' }}>
        <Box sx={{ textAlign: 'left', display: 'flex', height: '100%' }}>
          <Box sx={{ margin: '30px 30px 30px 50px' }}>
            <Box
              sx={{
                fontStyle: 'normal',
                marginBottom: '15px',
                fontWeight: '400',
                fontSize: '34px',
                lineHeight: '41px',
                letterSpacing: '0.01em',
                color: '#CED0D4',
              }}
            >
              {title}
            </Box>
            {/* {points.map((k:String,v:number) => {
              return (
                <Box sx={{marginBottom: '10px', fontStyle: 'normal', fontWeight: '400', fontSize: '16px', lineHeight: '19px', letterSpacing: '0.01em', color: '#CED0D4'}}>
                  {k}
                </Box>
              )
            })} */}
            {TextBox(points)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Template6 = (
  props: TemplateProps & { tutorialOpacity1?: number; tutorialOpacity2?: number },
) => {
  const { title, points, srcs, tutorialOpacity1, tutorialOpacity2 } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
      }}
    >
      <Box
        sx={{
          margin: '10px 30px 9px 30px',
          maxHeight: '50%',
          maxWidth: '100%', //backgroundColor: '#E2E2E2',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex', //marginTop: '5px',
            maxWidth: '100%',
            maxHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            style={{
              maxHeight: '100%',
              maxWidth: '50%',
              borderRadius: '10px',
              marginRight: '10px',
              opacity: tutorialOpacity1,
              transition: tutorialOpacity1 == 1 ? 'opacity 1s ease-in' : '',
            }}
            src={`https://${srcs[0]}`}
          />
          <img
            style={{
              maxHeight: '100%',
              maxWidth: '50%',
              borderRadius: '10px',
              marginLeft: '10px',
              opacity: tutorialOpacity2,
              transition: tutorialOpacity2 == 1 ? 'opacity 1s ease-in' : '',
            }}
            src={`https://${srcs[1]}`}
          />
        </Box>
      </Box>
      {/* Template 6 */}
      <Box
        sx={{
          margin: '0 auto auto auto',
          minHeight: points.length > 3 ? '325px' : '225px',
          height: points.length > 3 ? '325px' : '225px',
        }}
      >
        <Box sx={{ textAlign: 'left', display: 'flex', height: '100%' }}>
          <Box sx={{ margin: '30px 30px 30px 50px' }}>
            <Box
              sx={{
                fontStyle: 'normal',
                marginBottom: '15px',
                fontWeight: '400',
                fontSize: '34px',
                lineHeight: '41px',
                letterSpacing: '0.01em',
                color: '#CED0D4',
              }}
            >
              {title}
            </Box>
            {/* {points.map((k:String,v:number) => {
              return (
                <Box sx={{marginBottom: '10px', fontStyle: 'normal', fontWeight: '400', fontSize: '16px', lineHeight: '19px', letterSpacing: '0.01em', color: '#CED0D4'}}>
                  {k}
                </Box>
              )
            })} */}
            {TextBox(points)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const TextBox = (textArr: string[]) => {
  return textArr.map((k: string, v: number) => {
    return (
      <Box
        key={`text-box-${v}`}
        sx={{
          marginBottom: '10px',
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '17px',
          lineHeight: '19px',
          letterSpacing: '0.01em',
          color: '#CED0D4',
        }}
      >
        {k
          .replace(/<\/b>/g, '<b>')
          .split('<b>')
          .map((v, i) => {
            if (i % 2 == 1) {
              return <b key={`b-${v}-${i}`}>{v}</b>;
            } else {
              return v;
            }
          })}
      </Box>
    );
  });
};

export const Header = ({
  chaptersVisible,
  setChaptersVisible,
  onBack,
}: {
  chaptersVisible: boolean;
  setChaptersVisible: (visible: boolean) => void;
  /** The tutorial's Back (see `useTutorialBack`). */
  onBack: () => void;
}) => {
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { levelSelect } = data;
  const { levelData } = data;
  const levelName = levelData[levelSelect].name;
  const levelNumber = levelData[levelSelect].level_number;
  // The chapter the player is on, for the skip event (0 until one is saved).
  const lastChapter = useSelector(
    (state: RootState) =>
      (Immutable.get(state.tutorialReducer?.lastChapter, levelNumber) as number | undefined) ?? 0,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const chevronContainerStyles = {
    position: 'absolute',
    height: '100%',
  };

  const positionData = [
    { til: 4, src: Chevron1 },
    { til: 3, src: Chevron1 },
    { til: 2, src: Chevron1 },
    { til: 1, src: ChevronSingle },
  ];

  const mobileScreen = useMediaQuery(mobileQuery);
  // Pressed states for the header's plain controls (see usePressedState).
  const chaptersTogglePress = usePressedState();
  const backPress = usePressedState();
  const skipPress = usePressedState();

  const skip = () => {
    console.log('[Tutorial] Skip Tutorial clicked - cleaning up before navigation');
    dispatch(eventActions.tutorialSkippedEventAction(levelNumber, lastChapter));
    // Send unload event to clean up any active Phaser scenes
    NotifyPhaserEvents.UnloadExercise();
    console.log('[Tutorial] Sent UnloadExercise event to Phaser');

    // Small delay to allow cleanup to complete before navigation
    setTimeout(() => {
      if (data.levelData[levelSelect].level_number === 0) {
        // First-time intro tutorial: Skip sends the user to the roadmap (the same
        // destination as the Back button), not on to the next level's tutorial.
        // Do not advance levelSelect - the roadmap reads it to pick the selected
        // level, so advancing would land the user on the wrong level.
        console.log('[MUS-97] Tutorial skip (first-time) → /roadmap');
        navigate(`/roadmap`);
      } else {
        console.log('[Tutorial] Navigating to lesson');
        navigate(`/lesson`);
      }
    }, 100);
  };

  if (mobileScreen)
    return (
      <Box
        id="tutorial-header-mobile"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '50px',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          // The bar spans the screen above everything else; only its own controls take taps, so
          // a prompt or exercise under its empty band stays tappable.
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0 10px',
            marginTop: '10px',
          }}
        >
          <Box
            sx={{
              padding: '10px',
              background: '#CA7C62',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px',
              pointerEvents: 'auto',
              ...chaptersTogglePress.pressedSx,
            }}
            {...chaptersTogglePress.pressHandlers}
            onClick={() => setChaptersVisible(!chaptersVisible)}
          >
            {chaptersVisible ? <Close sx={{ color: 'white' }} /> : <MenuIcon color="white" />}
          </Box>
          <Box
            sx={{
              transition: '0.3s ease-in-out',
              opacity: chaptersVisible ? 1 : 0,
              pointerEvents: chaptersVisible ? 'auto' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                padding: '10px',
                background: '#CA7C62',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
                ...backPress.pressedSx,
              }}
              {...backPress.pressHandlers}
              onClick={onBack}
            >
              <ArrowBack sx={{ color: 'white' }} />
            </Box>
            <Typography variant="h6" color="#CA7C62">
              Level {levelNumber}: {levelName}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ padding: '0 10px', marginTop: '10px' }}>
          <Box
            sx={{
              padding: '10px',
              background: '#CA7C62',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: '0.3s ease-in-out',
              opacity: chaptersVisible ? 1 : 0,
              pointerEvents: chaptersVisible ? 'auto' : 'none',
            }}
          >
            <SkipNextRounded
              sx={{ color: 'white', ...skipPress.pressedSx }}
              {...skipPress.pressHandlers}
              onClick={skip}
            />
          </Box>
        </Box>
      </Box>
    );

  return (
    // <Box sx={{height: '160px', minHeight: '160px', display:'flex',flexDirection:'row', backgroundColor: 'red', position: 'relative'}}>
    <Box className="header-styles">
      <Box
        sx={{
          background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Box
            sx={{ ...backcontainerStyles, ...backPress.pressedSx }}
            {...backPress.pressHandlers}
            onClick={() => onBack()}
          >
            <img style={{ height: '100%' }} src={LeftArrowIcon} />
          </Box>
          <Box sx={headerTextStyles}>
            Level {levelNumber}: {levelName}
          </Box>
          <Box
            sx={{
              marginLeft: '25px',
              fontStyle: 'normal',
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: `${SKIP_TUTORIAL_LINE_PX}px`,
              textAlign: 'center',
              letterSpacing: '0.01em',
              textDecorationLine: 'underline',
              color: '#ADB1B7',
              '&:hover': {
                cursor: 'pointer',
              },
              ...skipTutorialTouchSx,
              ...skipPress.pressedSx,
            }}
            {...skipPress.pressHandlers}
            onClick={skip}
          >
            Skip Tutorial
          </Box>
        </Box>
      </Box>

      <Box className="header-container-styles">
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
            position: 'relative',
          }}
        >
          <Box className="tutorial-first-chevron-styles" />
          {positionData.map((v, idx) => {
            const imgStyle = { height: '100%' };
            const imgSrc = v.src;
            return (
              <Box
                key={uuid()}
                className={`tutorial-left-position-${idx + 1}`}
                sx={{ ...chevronContainerStyles }}
              >
                <img style={imgStyle} src={imgSrc} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const headerTextStyles = {
  color: '#CA7C62',
  //fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '24px',
  letterSpacing: '0.01em',
};

const backcontainerStyles = {
  marginLeft: '80px',
  marginRight: '18px',
  height: '25px',
  '&:hover': {
    background: 'rgba(222,144,118,.5)',
    cursor: 'pointer',
    transition: '0.3s',
    borderRadius: '50%',
    boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
  },
};

type FooterProps = {
  currentCarousel: number;
  setCurrentCarousel: React.Dispatch<React.SetStateAction<number>>;
  totalCarousels: number;
  onBackOnePage: () => void;
  incTutorial: () => void;
  setTutorialOpacity1: React.Dispatch<React.SetStateAction<number>>;
  setTutorialOpacity2: React.Dispatch<React.SetStateAction<number>>;
};

const Footer = (props: FooterProps) => {
  const {
    currentCarousel,
    setCurrentCarousel: _setCurrentCarousel,
    totalCarousels,
    onBackOnePage,
    incTutorial,
    setTutorialOpacity1: _setTutorialOpacity1,
    setTutorialOpacity2: _setTutorialOpacity2,
  } = props;
  const _navigate = useNavigate();
  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: '#050B13',
        height: '100px',
        minHeight: '100px',
        justifyContent: 'space-between',
        alignItems: 'center',
        // justifyContent: 'center',
        position: 'relative',
      }}
    >
      {!isCapacitorNative() && (
        <Box sx={{ position: 'absolute', top: 34, left: 30 }}>
          <Fullscreen show_label={false} />
        </Box>
      )}
      <Box sx={{ marginLeft: '54px', marginRight: '-54px' }}>
        <Button
          sx={{ visibility: currentCarousel > 1 ? '' : 'hidden' }}
          style={backButtonStyles}
          variant="contained"
          onClick={onBackOnePage}
        >
          Back
        </Button>
      </Box>
      <Box sx={{ display: 'flex', marginLeft: '10px' }}>
        {/* This will export the number of components based on the content... */}
        {[...Array(totalCarousels)].map((k, v) => {
          return (
            <Box
              key={`carousel-${v}`}
              sx={{
                width: '12px',
                height: '12px',
                backgroundColor: v == currentCarousel - 1 ? '#CA7C62' : '#5B646E',
                borderRadius: '50%',
                marginRight: '10px',
              }}
            />
          );
        })}
      </Box>

      <Box>
        <Button
          style={nextButtonStyles}
          variant="contained"
          onClick={() => {
            // setTutorialOpacity1(0)
            // setTutorialOpacity2(0)
            incTutorial();
          }}
        >
          {totalCarousels == currentCarousel ? 'Start' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

const nextButtonStyles = {
  margin: '15px 40px 15px 40px',
  width: '114px',
  color: 'black',
  fontSize: '16px',
  background: '#CA7C62',
  boxShadow:
    '0px 3.61149px 1.20383px -2.40766px rgba(0, 0, 0, 0.2), 0px 2.40766px 2.40766px rgba(0, 0, 0, 0.14), 0px 1.20383px 6.01915px rgba(0, 0, 0, 0.12)',
  borderRadius: '40px',
};
const backButtonStyles = {
  margin: '15px 40px 15px 40px',
  width: '114px',
  color: '#CA7C62',
  fontSize: '16px',
  border: '2px solid #CA7C62',
  background: 'none',
  //boxShadow: '0px 3.61149px 1.20383px -2.40766px rgba(0, 0, 0, 0.2), 0px 2.40766px 2.40766px rgba(0, 0, 0, 0.14), 0px 1.20383px 6.01915px rgba(0, 0, 0, 0.12);',
  borderRadius: '40px',
};

export default Tutorial;
